import { useEffect, useRef, useState, type ReactElement } from 'react';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

import { CardLayoutComponent } from '@workinance/shared/ui/CardLayoutComponent';
import {
	ChevronDownIcon,
	LogoutIcon,
	type IIconProps,
} from '@workinance/shared/ui/icons';

export interface IUserMenuItem {
	label: string;
	icon: (props: IIconProps) => ReactElement;
	onSelect: () => void;
}

interface IUserMenuComponentProps {
	firstName?: string;
	surName?: string;
	/** The line under the name, such as the person's role or their business. */
	subtitle?: string;
	/** Links shown above Log out. */
	items?: IUserMenuItem[];
	/** Shows a Log out button at the bottom when given. */
	onLogout?: () => void;
	/**
	 * `avatar`: a round initials button for a top bar, opening downward. `sidebar`: a full-width row with
	 * the name and subtitle, for the foot of a side navigation, opening upward.
	 */
	placement?: 'avatar' | 'sidebar';
}

const menuItemClasses =
	'flex w-full cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-medium transition-colors';

/** The standard account menu in every app: who is signed in, a few links, and optionally log out. */
export function UserMenuComponent({
	firstName = '',
	surName = '',
	subtitle,
	items = [],
	onLogout,
	placement = 'avatar',
}: IUserMenuComponentProps) {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;

		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const fullName = `${firstName} ${surName}`.trim();
	const initials = `${firstName[0] ?? ''}${surName[0] ?? ''}`.toUpperCase();

	return (
		<div ref={containerRef} className="relative">
			{placement === 'sidebar' ? (
				<button
					type="button"
					aria-expanded={isOpen}
					onClick={() => setIsOpen((open) => !open)}
					className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-rule bg-panel p-2 text-left transition-colors hover:bg-brand-soft/60"
				>
					<span
						aria-hidden="true"
						className="grid size-8 shrink-0 place-items-center rounded-full bg-ink text-xs font-bold text-paper"
					>
						{initials}
					</span>
					<span className="flex min-w-0 flex-1 flex-col leading-tight">
						<span className="truncate text-sm font-semibold">{fullName}</span>
						{subtitle && (
							<span className="truncate text-xs text-mute">{subtitle}</span>
						)}
					</span>
					<ChevronDownIcon
						className={parseClassnames(
							'size-4 shrink-0 text-mute transition-transform',
							{ 'rotate-180': !isOpen },
						)}
					/>
				</button>
			) : (
				<button
					type="button"
					aria-label="Account menu"
					aria-expanded={isOpen}
					onClick={() => setIsOpen((open) => !open)}
					className="grid size-10 cursor-pointer place-items-center rounded-full bg-brand text-sm font-bold text-on-brand transition-opacity hover:opacity-80"
				>
					{initials}
				</button>
			)}

			{isOpen ? (
				<CardLayoutComponent
					className={parseClassnames(
						'absolute z-50 rounded-3xl p-2 shadow-xl',
						{
							'top-full right-0 mt-2 min-w-64': placement === 'avatar',
							'inset-x-0 bottom-full mb-2': placement === 'sidebar',
						},
					)}
				>
					<div className="px-3 py-2.5">
						<p className="truncate font-semibold">{fullName}</p>
						{subtitle && (
							<p className="truncate text-sm text-mute">{subtitle}</p>
						)}
					</div>
					<div className="my-1 h-px bg-rule" />
					{items.map(({ label, icon: Icon, onSelect }) => (
						<button
							key={label}
							type="button"
							onClick={() => {
								setIsOpen(false);
								onSelect();
							}}
							className={parseClassnames(
								menuItemClasses,
								'hover:bg-brand-soft',
							)}
						>
							<Icon className="size-4 shrink-0 text-mute" />
							{label}
						</button>
					))}
					{onLogout && (
						<button
							type="button"
							onClick={onLogout}
							className={parseClassnames(
								menuItemClasses,
								'text-danger hover:bg-danger-soft',
							)}
						>
							<LogoutIcon className="size-4 shrink-0" />
							Log out
						</button>
					)}
				</CardLayoutComponent>
			) : null}
		</div>
	);
}
